'use client';

import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx/lite';
import { useCallback, useMemo, useState } from 'react';
import { CaretIcon } from '~/components/Icons';
import { getColumnMetadata } from '~/utils/getColumnMetadata';
import { Container } from '../../Container';
import { CustomDropdown } from '../../CustomDropdown';
// Column definitions per stat type
import { STATS_CONFIG } from '~/config/statsConfig';
// Stats metadata for use in table columns
import statsMetaData from '~/data/statsMetaData.json';

// Memoize column creation
const columnHelper = createColumnHelper();
const createColumns = (columns, statsMetaData) => {
  // Map columns with their metadata
  const columnsWithMeta = columns.map((col) => {
    // First get metadata by matching property name and statTableId for the current table type
    const metadata = getColumnMetadata(
      col.accessor,
      statsMetaData,
      col.statTableId,
    );

    return {
      columnDef: columnHelper.accessor(col.accessor, {
        header: metadata?.headerText || col.header,
        cell: (info) => {
          const value = info.getValue();

          // Handle null/undefined values
          if (value === null || value === undefined || value === 0) {
            return <div>-</div>;
          }

          // Apply precision formatting if specified in metadata
          if (metadata?.precision !== undefined && typeof value === 'number') {
            const formatted = value.toFixed(metadata.precision);
            return (
              <div>
                {metadata.leadingZeroFlg
                  ? formatted
                  : formatted.replace(/^0/, '')}
              </div>
            );
          }

          return <div>{value}</div>;
        },
        sortingFn: customSortingFn,
        enableSorting: true,
        sortDescFirst: true,
        meta: {
          ...col.meta,
          tooltip: metadata?.toolTipText,
          isPercentage: metadata?.percentFlg,
          seqNo: metadata?.seqNo || 999, // Use metadata seqNo for sorting
        },
      }),
      seqNo: metadata?.seqNo || 999, // Store seqNo for sorting columns
      isYear: col.accessor === 'year', // Special case for year column
    };
  });

  // Always put year column first, then sort rest by seqNo
  const yearColumn = columnsWithMeta.find((col) => col.isYear);
  const otherColumns = columnsWithMeta
    .filter((col) => !col.isYear)
    .sort((a, b) => a.seqNo - b.seqNo);

  // Return the sorted columnDefs
  return yearColumn
    ? [yearColumn.columnDef, ...otherColumns.map((col) => col.columnDef)]
    : otherColumns.map((col) => col.columnDef);
};

// Custom sorting function
const customSortingFn = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  // Handle numeric values
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  // Handle string values
  return (a ?? '').toString().localeCompare((b ?? '').toString());
};

// transformStats and memoize results
const transformStats = (stats, type, seasons) => {
  if (!stats?.length) return [];

  return stats
    .map((season) => {
      // Only process entries with statType === "Season Stats" or "Career Stats"
      if (
        !season ||
        !season[type]?.length ||
        !season[type].some(
          (stat) =>
            stat.statType === 'Season Stats' ||
            stat.statType === 'Career Stats',
        )
      )
        return null;

      // For fielding stats, find the appropriate entry
      const statData = season[type].find(
        (stat) =>
          // Use Career Stats for career totals, otherwise use Season Stats
          (!season.seasonId && stat.statType === 'Career Stats') ||
          (season.seasonId && stat.statType === 'Season Stats'),
      );

      // If no appropriate stats found, skip this entry
      if (!statData) return null;

      const isTotal = !season.seasonId;
      const seasonInfo = seasons.find((s) => s?.seasonId === season?.seasonId);

      return {
        year: isTotal ? 'Career Totals' : seasonInfo?.description || '',
        seasonType: seasonInfo?.seasonType || '',
        isTotal,
        ...Object.fromEntries(
          Object.entries(statData || {}).map(([key, value]) => [key, value]),
        ),
      };
    })
    .filter(Boolean);
};

// StatsButton component
const StatsButton = ({ type, activeTable, onClick }) => (
  <div className="flex items-center justify-center gap-1">
    <button
      data-target={type}
      className={clsx(
        'rounded text-center font-nova text-xl font-bold uppercase leading-tight tracking-[1.5px] underline-offset-4 lg:border lg:border-[#ababab] lg:px-6 lg:py-4',
        activeTable === STATS_CONFIG[type].id
          ? 'text-black underline lg:bg-black lg:text-white lg:no-underline'
          : 'text-[#ababab]',
      )}
      onClick={() => onClick(type)}
    >
      {STATS_CONFIG[type].label}
    </button>
  </div>
);

// Table component
const StatsTable = ({ table, isVisible }) => (
  <div className={clsx('relative overflow-x-auto', !isVisible && 'hidden')}>
    <table className="-my-1 min-w-full border-separate border-spacing-y-1 text-left text-[18px] font-semibold leading-9 tracking-[1.5px]">
      <thead className="bg-[--theme-secondary]">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className={clsx(
                  'cursor-pointer select-none whitespace-nowrap px-3 py-3 font-nova text-lg font-semibold uppercase leading-tight tracking-wider text-[--theme-action]',
                  header.column.id === 'year' &&
                    'sticky left-0 z-10 bg-[--theme-secondary]',
                )}
                onClick={header.column.getToggleSortingHandler()}
              >
                <div className="flex items-center gap-x-2">
                  {header.column.columnDef.header}
                  <CaretIcon
                    className={clsx(
                      'w-3',
                      !header.column.getIsSorted()
                        ? 'invisible'
                        : header.column.getIsSorted() === 'asc'
                          ? 'rotate-180'
                          : null,
                    )}
                  />
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody className="[&_td]:whitespace-nowrap [&_td]:px-3 [&_td]:py-3">
        {table
          .getRowModel()
          .rows.filter((row) => !row.original.isTotal)
          .map((row) => (
            <tr key={row.id} className="border-b-2 border-white bg-[#f0f0f0]">
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={clsx(
                    'whitespace-nowrap px-6 py-3 font-nova text-lg font-semibold leading-tight tracking-wider text-black',
                    cell.column.id === 'year' &&
                      'sticky left-0 z-10 bg-[#f0f0f0]',
                  )}
                >
                  {cell.column.columnDef.cell(cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
      </tbody>
      <tfoot>
        {table
          .getRowModel()
          .rows.filter((row) => row.original.isTotal)
          .map((row) => (
            <tr
              key={row.id}
              className="cursor-default border-b-2 border-white bg-[#e0e0e0]"
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={clsx(
                    'whitespace-nowrap px-3 py-3 font-nova text-lg font-semibold leading-tight tracking-wider text-black',
                    cell.column.id === 'year' &&
                      'sticky left-0 z-10 bg-[#e0e0e0]',
                  )}
                >
                  {cell.column.columnDef.cell(cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
      </tfoot>
    </table>
  </div>
);

// Main component
export function PlayerCareerStatsTable({ data, seasons }) {
  // Transform the stats
  const stats = useMemo(
    () => ({
      battingStats: transformStats(data, 'battingStats', seasons),
      fieldingStats: transformStats(data, 'fieldingStats', seasons),
      pitchingStats: transformStats(data, 'pitchingStats', seasons),
    }),
    [data, seasons],
  );

  // Initialize sorting state
  const [sorting, setSorting] = useState([{ id: 'year', desc: true }]);

  // First, define seasonTypeOptions
  const seasonTypeOptions = useMemo(() => {
    const types = new Set();
    Object.values(stats).forEach((statArray) => {
      statArray
        .filter((stat) => !stat.isTotal)
        .forEach((stat) => {
          if (stat?.seasonType) {
            types.add(stat.seasonType);
          }
        });
    });

    return Array.from(types)
      .map((type) => ({
        value: type,
        label: type === 'Championship' ? 'All-Star Cup' : 'AUX Softball',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [stats]);

  // Initialize selectedTypes with all season types
  const [selectedTypes, setSelectedTypes] = useState(() =>
    seasonTypeOptions.map((option) => option.value),
  );

  // Now get available stats with season type filtering
  const availableStats = useMemo(
    () =>
      Object.keys(STATS_CONFIG).filter((type) => {
        const statsForType = stats[STATS_CONFIG[type].id];
        if (!statsForType?.length) return false;

        // If a season type is selected, check if there's data for that type
        if (selectedTypes.length > 0) {
          return statsForType.some(
            (stat) => !stat.isTotal && selectedTypes.includes(stat.seasonType),
          );
        }
        return true;
      }),
    [stats, selectedTypes],
  );

  // Get initial active table based on available stats
  const initialActiveTable = useMemo(() => {
    if (
      data[0]?.primaryPosition?.positionLk === 'RHP' ||
      data[0]?.primaryPosition?.positionLk === 'LHP'
    ) {
      if (availableStats.includes('pitching')) {
        return STATS_CONFIG.pitching.id;
      }
    }
    const firstAvailableStat = availableStats[0];
    return firstAvailableStat ? STATS_CONFIG[firstAvailableStat].id : '';
  }, [availableStats, data]);

  const [activeTable, setActiveTable] = useState(initialActiveTable);

  // Memoize active configuration with corrected year filtering
  const activeConfig = useMemo(() => {
    const type = Object.values(STATS_CONFIG).find(
      (config) => config.id === activeTable,
    );

    if (!type) {
      return {
        data: [],
        columns: [],
      };
    }

    let filteredData = stats[activeTable];
    if (selectedTypes.length > 0) {
      // Separate career totals and regular stats
      const careerTotals = filteredData.filter((row) => row.isTotal);
      const regularStats = filteredData.filter(
        (row) => !row.isTotal && selectedTypes.includes(row.seasonType),
      );

      // Show career totals if all season types are selected or none are selected
      const showCareerTotals =
        selectedTypes.length === 0 ||
        selectedTypes.length === seasonTypeOptions.length;

      // Combine regular stats with career totals if conditions are met
      filteredData = showCareerTotals
        ? [...regularStats, ...careerTotals]
        : regularStats;
    }

    // If there's no data for the current active table after filtering,
    // find the first available stat type that has data
    if (!filteredData?.length && availableStats.length > 0) {
      const firstAvailableType = availableStats[0];
      const newType = Object.values(STATS_CONFIG).find(
        (config) => config.id === STATS_CONFIG[firstAvailableType].id,
      );

      // Update active table state
      setActiveTable(STATS_CONFIG[firstAvailableType].id);

      // Get filtered data for the new type
      filteredData = stats[STATS_CONFIG[firstAvailableType].id];
      if (selectedTypes.length > 0) {
        filteredData = filteredData.filter(
          (row) => !row.isTotal && selectedTypes.includes(row.seasonType),
        );
      }

      // Add the tableId to columns that don't have a statTableId
      const columnsWithTableId = newType.columns.map((col) => ({
        ...col,
        statTableId:
          col.statTableId !== undefined ? col.statTableId : newType.tableId,
      }));

      return {
        data: filteredData,
        columns: createColumns(columnsWithTableId, statsMetaData),
      };
    }

    // Add the tableId to columns that don't have a statTableId
    const columnsWithTableId = type.columns.map((col) => ({
      ...col,
      statTableId:
        col.statTableId !== undefined ? col.statTableId : type.tableId,
    }));

    return {
      data: filteredData,
      columns: createColumns(columnsWithTableId, statsMetaData),
    };
  }, [activeTable, stats, selectedTypes, availableStats]);

  // Memoize table instance
  const table = useReactTable({
    data: activeConfig.data,
    columns: activeConfig.columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
  });

  // Memoize button click handler
  const handleButtonClick = useCallback((type) => {
    setActiveTable(STATS_CONFIG[type].id);
    setSorting([{ id: 'year', desc: true }]);
    setSelectedTypes([]); // Reset the selected years when changing stat types
  }, []);

  return (
    <Container className="relative gap-6 py-5 lg:my-16">
      <div className="filter-wrap flex-row items-end justify-between gap-x-6 lg:flex">
        <div className="filter-title flex w-full flex-col justify-between lg:flex-row lg:items-center">
          <h2 className="shrink grow basis-0 font-nova text-4xl font-black uppercase text-black">
            Career Stats
          </h2>
        </div>
      </div>
      <div className="mr-auto flex w-full flex-wrap items-center gap-x-6 lg:flex-nowrap lg:gap-1">
        {availableStats.map((type) => (
          <StatsButton
            key={type}
            type={type}
            activeTable={activeTable}
            onClick={handleButtonClick}
          />
        ))}
        <CustomDropdown
          options={seasonTypeOptions}
          label="Season Type"
          value={selectedTypes}
          onChange={setSelectedTypes}
          allLabel={false}
          className="ml-auto"
          collapsible
          multiple
        />
      </div>
      <StatsTable table={table} isVisible={true} />
      <p className="text-gray-500 text-sm">
        *The AUSL All-Star Cup was known as the Championship season through
        2024.
      </p>
    </Container>
  );
}
